/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { BUTTON_ALIGNMENT, FIRST_LAYER_VARIANT, UI_VARIANT } from '@usercentrics/cmp-browser-sdk';
import { ButtonElement, ButtonsContainerProps, ButtonsWrapperProps, WrapperContainerProps } from './interface';
import { CSS_SELECTOR, useCustomCss } from '../../contexts/CustomCss';
import { Container, MainButtonsContainer, RowContainer } from './ButtonsContainer.styles';
import { Fragment, FunctionalComponent, VNode, h } from 'preact';
import { filterButtons, isCcpaButtons, isDefaultButtons, isTCFButtons } from './helpers';
import { HorizontalSpacer } from '../Spacers/HorizontalSpacer';
import { VIEW } from '../../enums';
import { VerticalSpacer } from '../Spacers/VerticalSpacer';
import { memo } from 'preact/compat';
import { useDeviceState } from '../../hooks';
import { useTheme } from 'styled-components';
import { useUiContext } from '../../contexts/UiContext';

/*
  This component is responsible for the Buttons arrangement.
  
  DESKTOP - exact listed order
  - CCPA: More Button**, Toggle and button on the first line
  - Default: More Button*, Save Button*, Deny All* and Accept All on the same line
  - TCF: Save, Deny All* and Accept All on the same line

  MOBILE - exact listed order with divider in between the lines
  - CCPA: Toggle and button on FIRST line, More Button** on second line
  - Default:
    Accept All and Deny All* on first line

    If we display only Accept All on the first line:
    More Button* and Save Button* on the second line

    If we display Accept All and Deny All on the first line:
    Save Button* on the second line
    More Button* on the third line (which can be on the second if there is no Save Button)

  TCF: Deny All* and Accept All on the first line and Save on its own line.

  * if enabled
  ** if enabled, only displayed in the first layer
*/

/**
 * This wrapper is responsible of building the arrangement of the Accept All, Deny All and Toggle depending on the
 * variant and device type (mobile or desktop) as well as on string length of the labels
 * @param buttons the buttons array
 * @param isMobile boolean to define if device is mobile or not
 * @param variant the ui variant
 */
const ButtonsWrapper: FunctionalComponent<ButtonsWrapperProps> = memo(({ buttons = [], testid = '' }) => {
  const { rules } = useCustomCss(CSS_SELECTOR.BUTTONS_CONTAINER);
  const { isDesktop, isMobile, isPortrait } = useDeviceState();
  const { uiVariant, firstLayerVariant, view } = useUiContext();
  const theme = useTheme();
  const { alignment } = theme.ui.buttons;
  const displayInSeperateRows = buttons.reduce(
    (oneBigLabelFound, button) => oneBigLabelFound || (button.props.label != null && button.props.label.length > 17),
    false,
  );
  const useAlignment =
    isDesktop &&
    firstLayerVariant === FIRST_LAYER_VARIANT.WALL &&
    uiVariant === UI_VARIANT.DEFAULT &&
    view === VIEW.FIRST_LAYER;

  const WrapperContainer: FunctionalComponent<WrapperContainerProps> = ({ children, id }) =>
    displayInSeperateRows ? (
      <div style={{ ...rules }}>{children}</div>
    ) : (
      <RowContainer data-testid={id} useAlignment={useAlignment} style={{ ...rules }}>
        {children}
      </RowContainer>
    );

  return (
    <WrapperContainer id={testid}>
      {buttons.map((button, index) => {
        if (index === buttons.length - 1) {
          return <Fragment>{button}</Fragment>;
        }

        return (
          <Fragment>
            {button}
            {(displayInSeperateRows && isMobile && isPortrait) ||
            (useAlignment && alignment === BUTTON_ALIGNMENT.VERTICAL) ? (
              <VerticalSpacer medium={uiVariant === UI_VARIANT.CCPA} small />
            ) : (
              <HorizontalSpacer large={uiVariant === UI_VARIANT.CCPA} small={isMobile} medium={isDesktop} />
            )}
          </Fragment>
        );
      })}
    </WrapperContainer>
  );
});

// Always display the buttons in the same order
// Only depending on the ui variant
const DesktopButtons: FunctionalComponent<ButtonsContainerProps> = memo((props) => {
  const { uiVariant } = useUiContext();

  let buttonsArray: VNode<ButtonElement>[] = [];

  switch (uiVariant) {
    case UI_VARIANT.CCPA: {
      if (isCcpaButtons(props)) {
        const { CcpaToggle, CcpaButton } = props;
        buttonsArray = filterButtons([CcpaToggle, CcpaButton]);
      }
      break;
    }
    case UI_VARIANT.TCF: {
      if (isTCFButtons(props)) {
        const { AcceptAllButton, DenyAllButton, SaveButton } = props;
        buttonsArray = filterButtons([SaveButton, DenyAllButton, AcceptAllButton]);
      }
      break;
    }
    case UI_VARIANT.DEFAULT:
    default: {
      if (isDefaultButtons(props)) {
        const { AcceptAllButton, DenyAllButton, SaveButton, MoreButton } = props;
        buttonsArray = filterButtons([MoreButton, SaveButton, DenyAllButton, AcceptAllButton]);
      }
    }
  }

  if (buttonsArray.length === 0) {
    return null;
  }

  return <ButtonsWrapper buttons={buttonsArray} testid="uc-buttons-container" />;
});

// Show buttons in a different order
// Depending on the number of buttons and which button should be display
const MobileButtons: FunctionalComponent<ButtonsContainerProps> = memo((props) => {
  const { rules } = useCustomCss(CSS_SELECTOR.BUTTONS_CONTAINER);
  const { uiVariant } = useUiContext();
  const { isPortrait } = useDeviceState();

  let firstLineButtons: VNode<ButtonElement>[] = [];
  let secondLineButtons: VNode<ButtonElement>[] = [];
  let thirdLineButtons: VNode<ButtonElement>[] = [];
  let displayInSeperateRows = false;

  switch (uiVariant) {
    case UI_VARIANT.CCPA: {
      if (isCcpaButtons(props)) {
        const { CcpaToggle, CcpaButton } = props;
        displayInSeperateRows = true;

        if (CcpaToggle) {
          firstLineButtons = filterButtons([CcpaToggle]);
          secondLineButtons = filterButtons([CcpaButton]);
        } else {
          firstLineButtons = filterButtons([CcpaButton]);
        }
      }
      break;
    }
    case UI_VARIANT.TCF: {
      if (isTCFButtons(props)) {
        const { AcceptAllButton, DenyAllButton, SaveButton } = props;
        firstLineButtons = filterButtons([DenyAllButton, AcceptAllButton]);
        secondLineButtons = filterButtons([SaveButton]);
        displayInSeperateRows = true;
      }
      break;
    }
    case UI_VARIANT.DEFAULT:
    default: {
      if (isDefaultButtons(props)) {
        const { AcceptAllButton, DenyAllButton, MoreButton, SaveButton } = props;
        firstLineButtons = filterButtons([DenyAllButton, AcceptAllButton]);

        if (isPortrait) {
          displayInSeperateRows = true;
        }

        if (firstLineButtons.length === 1) {
          // If only AcceptAll is displayed - save and more will be together in the second line
          secondLineButtons = filterButtons([SaveButton, MoreButton]);
        } else if (SaveButton && MoreButton) {
          // If we show DenyAll and AcceptAll on the first line
          // and we still need to show two buttons, the More button will go to the third line
          secondLineButtons = filterButtons([SaveButton]);
          thirdLineButtons = filterButtons([MoreButton]);
        } else {
          // If we show DenyAll and AcceptAll on the first line
          // but we only have one or none other buttons to display
          secondLineButtons = filterButtons([SaveButton || MoreButton]);
        }
      }
    }
  }

  if (firstLineButtons.length === 0) {
    return null;
  }

  return (
    <MainButtonsContainer direction={displayInSeperateRows ? 'column' : 'row'} style={{ ...rules }}>
      <ButtonsWrapper testid="first-line-buttons" buttons={firstLineButtons} />
      {secondLineButtons.length > 0 && (
        <Fragment>
          {displayInSeperateRows ? (
            <VerticalSpacer medium={uiVariant === UI_VARIANT.CCPA} small={uiVariant === UI_VARIANT.DEFAULT} />
          ) : (
            <HorizontalSpacer small />
          )}
          <ButtonsWrapper testid="second-line-buttons" buttons={secondLineButtons} />
        </Fragment>
      )}
      {thirdLineButtons.length > 0 && (
        <Fragment>
          {displayInSeperateRows ? (
            <VerticalSpacer medium={uiVariant === UI_VARIANT.CCPA} small={uiVariant === UI_VARIANT.DEFAULT} />
          ) : (
            <HorizontalSpacer small />
          )}
          <ButtonsWrapper testid="third-line-buttons" buttons={thirdLineButtons} />
        </Fragment>
      )}
    </MainButtonsContainer>
  );
});

const ButtonsContainer: FunctionalComponent<ButtonsContainerProps> = memo((props) => {
  const { isDesktop } = useDeviceState();
  const { uiVariant } = useUiContext();
  const isCcpa = uiVariant === UI_VARIANT.CCPA;

  return (
    <Container>
      {isCcpa && <VerticalSpacer small />}
      {isDesktop ? <DesktopButtons {...props} /> : <MobileButtons {...props} />}
    </Container>
  );
});

export default ButtonsContainer;
